// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {deferUntilModulesLoaded} from '@app/features/platform/utils/DeferUntilModulesLoaded';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import UserSettingsState, {type UserSettings} from '@app/features/user/state/UserSettings';
import {StickerAnimationOptions} from '@app/vendor/constants/UserConstants';
import {AccessibilityOverridesSchema} from '@app/vendor/schema/gen/fluxer/user/preferences/v1/accessibility_pb';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

const logger = new Logger('AccessibilityOverride');
export type AnimationOverrides = Readonly<{
	gifAutoPlayDirty: boolean;
	animateEmojiDirty: boolean;
	animateStickersDirty: boolean;
}>;
class AccessibilityOverride {
	gifAutoPlayDirty = false;
	animateEmojiDirty = false;
	animateStickersDirty = false;
	private overrideTimeout: number | null = null;
	private initialized = false;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.initPersistence();
		this.bindToAccessibilityState();
		setTimeout(() => {
			runInAction(() => {
				this.initialized = true;
				logger.debug('AccessibilityOverride initialized');
				if (Accessibility.useReducedMotion && UserSettingsState.isHydrated()) {
					this.applyReducedMotionOverrides();
				}
			});
		}, 1000);
	}
	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'accessibilityOverrides',
			schema: AccessibilityOverridesSchema,
			persist: ['gifAutoPlayDirty', 'animateEmojiDirty', 'animateStickersDirty'],
			toMessage: (s) => ({
				gifAutoplayDirty: s.gifAutoPlayDirty,
				animateEmojiDirty: s.animateEmojiDirty,
				animateStickersDirty: s.animateStickersDirty,
			}),
			applyMessage: (s, m) => {
				s.gifAutoPlayDirty = m.gifAutoplayDirty;
				s.animateEmojiDirty = m.animateEmojiDirty;
				s.animateStickersDirty = m.animateStickersDirty;
			},
		});
	}
	private bindToAccessibilityState(): void {
		deferUntilModulesLoaded(() => {
			reaction(
				() => Accessibility.useReducedMotion,
				(isReducedMotion, previous) => {
					if (previous === false && isReducedMotion) {
						this.scheduleReducedMotionOverrides();
					}
				},
				{fireImmediately: false},
			);
		});
	}
	private scheduleReducedMotionOverrides(): void {
		const schedule = () => {
			if (this.overrideTimeout != null) {
				clearTimeout(this.overrideTimeout);
			}
			this.overrideTimeout = window.setTimeout(() => {
				this.applyReducedMotionOverrides();
				this.overrideTimeout = null;
			}, 50);
		};
		schedule();
		window.setTimeout(schedule, 0);
		window.setTimeout(schedule, 10);
	}
	private applyReducedMotionOverrides(): void {
		if (!UserSettingsState.isHydrated()) {
			logger.debug('Skipping reduced motion overrides before user settings hydration');
			return;
		}
		if (!this.initialized) {
			logger.debug('Skipping reduced motion overrides during initialization');
			return;
		}
		const updates: Partial<UserSettings> = {};
		const {gifAutoPlay, animateEmoji, animateStickers} = UserSettingsState;
		if (!this.gifAutoPlayDirty && gifAutoPlay) {
			updates.gifAutoPlay = false;
		}
		if (!this.animateEmojiDirty && animateEmoji) {
			updates.animateEmoji = false;
		}
		if (!this.animateStickersDirty && animateStickers === StickerAnimationOptions.ALWAYS_ANIMATE) {
			updates.animateStickers = StickerAnimationOptions.ANIMATE_ON_INTERACTION;
		}
		if (Object.keys(updates).length > 0) {
			void UserSettingsState.saveSettings(updates).catch((error) => {
				logger.error('Failed to apply reduced motion overrides', error);
			});
		}
		this.gifAutoPlayDirty = false;
		this.animateEmojiDirty = false;
		this.animateStickersDirty = false;
		logger.debug('Applied reduced motion overrides');
	}
	updateUserSettings(userSettings: unknown): void {
		if (!this.initialized) {
			logger.debug('Skipping updateUserSettings during initialization');
			return;
		}
		const currentUserSettings = UserSettingsState;
		const isReducedMotion = Accessibility.useReducedMotion;
		if (!isReducedMotion) return;
		if (!isRecord(userSettings)) {
			return;
		}
		const gifAutoPlayValue = userSettings.gif_auto_play;
		const animateEmojiValue = userSettings.animate_emoji;
		const animateStickersValue = userSettings.animate_stickers;
		if (typeof gifAutoPlayValue === 'boolean' && gifAutoPlayValue !== currentUserSettings.gifAutoPlay) {
			this.gifAutoPlayDirty = true;
			logger.debug('Marked gifAutoPlay as dirty');
		}
		if (typeof animateEmojiValue === 'boolean' && animateEmojiValue !== currentUserSettings.animateEmoji) {
			this.animateEmojiDirty = true;
			logger.debug('Marked animateEmoji as dirty');
		}
		if (typeof animateStickersValue === 'number' && animateStickersValue !== currentUserSettings.animateStickers) {
			this.animateStickersDirty = true;
			logger.debug('Marked animateStickers as dirty');
		}
		this.applyReducedMotionOverrides();
	}
	markDirty(setting: keyof AnimationOverrides): void {
		this[setting] = true;
		logger.debug(`Marked ${setting} as dirty`);
	}
	isOverriddenByReducedMotion(setting: 'gif_auto_play' | 'animate_emoji' | 'animate_stickers'): boolean {
		const isReducedMotion = Accessibility.useReducedMotion;
		if (!isReducedMotion) return false;
		switch (setting) {
			case 'gif_auto_play':
				return !this.gifAutoPlayDirty;
			case 'animate_emoji':
				return !this.animateEmojiDirty;
			case 'animate_stickers':
				return !this.animateStickersDirty;
			default:
				return false;
		}
	}
	get effectiveGifAutoPlay(): boolean {
		if (this.isOverriddenByReducedMotion('gif_auto_play')) {
			return false;
		}
		return UserSettingsState.getGifAutoPlay();
	}
	get effectiveAnimateEmoji(): boolean {
		if (this.isOverriddenByReducedMotion('animate_emoji')) {
			return false;
		}
		return UserSettingsState.getAnimateEmoji();
	}
	get effectiveAnimateStickers(): number {
		if (this.isOverriddenByReducedMotion('animate_stickers')) {
			if (UserSettingsState.getAnimateStickers() === StickerAnimationOptions.ALWAYS_ANIMATE) {
				return StickerAnimationOptions.ANIMATE_ON_INTERACTION;
			}
		}
		return UserSettingsState.getAnimateStickers();
	}
}
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
export default new AccessibilityOverride();
